"use client";

import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import useVisibility from "@/utils/hooks/use-visibility";
import React, { useEffect } from "react";
import { useTranslation } from "@/app/i18n/client";
import type { BusRequest } from "@/types/busrequest";
import { Company } from "@/types/company";
import { AdvertiserLocation } from "@/types/location";
import dynamic from "next/dynamic";
import { MarkerType } from "@/components/Util/Map/Map";
import { cn } from "@/lib/utils";
import { useGoogleMaps } from "@/utils/google-maps-context";

const Map = dynamic(
  () => import(/* webpackChunkName: "map" */ "@/components/Util/Map/Map"),
);

interface Props {
  showClusterModal: (ids: string[]) => void;
  showSingleMarkerModal?: (id: string) => void;
  visibleItems: (BusRequest | AdvertiserLocation | Company)[];
  type: MarkerType;
  height: string;
  hideSearchField?: boolean;
}

export default function MapContainer(props: Props) {
  const { t } = useTranslation();
  const { isLoaded: isMapsLibLoaded, loadNow } = useGoogleMaps();
  const [isVisible, currentElement] = useVisibility<HTMLDivElement>();

  useEffect(() => {
    if (isVisible) {
      loadNow();
    }
  }, [isVisible, loadNow]);

  return (
    <div
      ref={currentElement}
      className={cn("map-container rounded-l-2xl", `h-[${props.height}]`)}
    >
      {isVisible && isMapsLibLoaded ? (
        <React.Suspense
          fallback={
            <div className="google-map-container">
              <LoadingSpinner
                embedded={true}
                label={t("LOADING.LABEL.MAP") ?? "Map"}
              />
            </div>
          }
        >
          <Map {...props} />
        </React.Suspense>
      ) : (
        <div className="google-map-container">
          <LoadingSpinner
            embedded={true}
            label={t("LOADING.LABEL.MAP") ?? "Map"}
          />
        </div>
      )}
    </div>
  );
}
